import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { SessionContext } from "context/sessionContext"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Input,
  Badge,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { promotionRequestsApi } from "apis/PromotionRequestsApi"
import Swal from "sweetalert2"
import Select from "react-select"
import { classApi } from "apis/ClassListApi"

const PromotedStudentReport = () => {
  document.title = "Promoted Student Report"

  const navigate = useNavigate()

  const { Session } = useContext(SessionContext)

  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(false)

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")
  const [selectedClass, setSelectedClass] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)

  // Export
  const [isExporting, setIsExporting] = useState(false)
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Class list
  const [classList, setClassList] = useState([])

  // Status filter options
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Promoted", label: "Promoted" },
    { value: "Rejected", label: "Rejected" },
  ]

  // ── Fetch class list ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await classApi.getAllClass()
        if (res?.data?.section_list && Array.isArray(res.data.section_list)) {
          const unique = res.data.section_list
            .map(cl => ({
              value: cl.UID,
              label: `${cl.st_class.toUpperCase()} / ${cl.st_sec.toUpperCase()}`,
              st_class: cl.st_class,
              st_sec: cl.st_sec,
            }))
            .filter(
              (item, index, arr) =>
                index === arr.findIndex(d => d.label === item.label)
            )
          setClassList(unique)
        }
      } catch (err) {
        console.error("Failed to fetch class list:", err.message)
      }
    }
    fetchClasses()
  }, [])

  const selectStyles = {
    menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
    menuList: provided => ({
      ...provided,
      backgroundColor: "#fff",
      maxHeight: "200px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
      color: "#000",
      cursor: "pointer",
    }),
  }

  // Fetch promoted students
  const fetchStudents = async (page = 1, search = "") => {
    const sessionId = Session || sessionStorage.getItem("SessionId")
    if (!sessionId) return
    setLoading(true)

    try {
      const res = await promotionRequestsApi.getPromotedStudent({
        session_id: parseInt(sessionId, 10),
        search: search || "",
        page,
        page_size: 20,
        st_class: selectedClass ? selectedClass.st_class : "",
        st_sec: selectedClass ? selectedClass.st_sec : "",
        status: selectedStatus ? selectedStatus.value : "All",
      })

      if (res?.data?.status) {
        setStudents(res.data.payload || [])
        setCurrentPage(res.data.page ?? page)
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_records ?? 0)
      } else {
        setStudents([])
        setTotalPages(1)
        setTotalCount(0)
      }
    } catch (err) {
      console.error("Failed to fetch promoted students:", err.message)
      setStudents([])
    } finally {
      setLoading(false)
    }
  }

  // Reset selection when session changes
  useEffect(() => {
    setSelectedClass(null)
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
  }, [Session])

  // Reload when session, search or selectedClass changes
  useEffect(() => {
    const sessionId = Session || sessionStorage.getItem("SessionId")
    if (sessionId) {
      fetchStudents(1, appliedSearch)
    }
  }, [Session, appliedSearch, selectedClass, selectedStatus])

  // Search Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setAppliedSearch(searchQuery)
    }, 500)
    return () => clearTimeout(delayDebounce)
  }, [searchQuery])

  // Pagination Change
  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchStudents(page, appliedSearch)
  }

  // Export handler
  const handleExport = async format => {
    const sessionId = Session || sessionStorage.getItem("SessionId")
    if (!sessionId) return
    setIsExporting(true)

    try {
      const res = await promotionRequestsApi.exportPromotedStudent({
        session_id: parseInt(sessionId, 10),
        search: appliedSearch || "",
        st_class: selectedClass ? selectedClass.st_class : "",
        st_sec: selectedClass ? selectedClass.st_sec : "",
        status: selectedStatus ? selectedStatus.value : "All",
        export_format: format,
      })

      if (res.data && res.data.type === "application/json") {
        const text = res.data.text ? await res.data.text() : await new Promise(r => {
          const reader = new FileReader()
          reader.onload = () => r(reader.result)
          reader.readAsText(res.data)
        })
        try {
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
        } catch (e) {
          Swal.fire("Error", "Failed to export report.", "error")
        }
        return
      }

      const fileType =
        format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf"
      const fileExtension = format === "excel" ? "xlsx" : "pdf"

      const blob = new Blob([res.data], { type: fileType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Promoted_Student_Report.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Export failed:", err)
      if (
        err.response?.data &&
        err.response.data instanceof Blob &&
        err.response.data.type === "application/json"
      ) {
        try {
          const text = err.response.data.text
            ? await err.response.data.text()
            : await new Promise(r => {
                const reader = new FileReader()
                reader.onload = () => r(reader.result)
                reader.readAsText(err.response.data)
              })
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
          return
        } catch (e) {
          // fallback
        }
      }
      Swal.fire("Error", "Failed to export report.", "error")
    } finally {
      setIsExporting(false)
    }
  }

  // Render Pagination Helper
  const renderPagination = () => {
    if (totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> | Total: <strong>{totalCount}</strong> students
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
          </li>
          {pages.map(p => (
            <li key={p} className={`page-item ${p === currentPage ? "active" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(p)}>{p}</button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="" breadcrumbItem="Promoted Student Report" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col xs={12} className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Promoted Students</h5>
                      <Button
                        color="secondary"
                        onClick={() => navigate("/promotion-requests")}
                      >
                        <i className="bx bx-arrow-back me-1" />
                        Back to Promotion Requests
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mb-4 align-items-center">
                    <Col sm={6} md={5} className="mb-3 mb-sm-0">
                      <div className="d-flex gap-2">
                        <div style={{ width: "200px" }}>
                          <Select
                            value={selectedClass}
                            onChange={setSelectedClass}
                            options={classList}
                            placeholder="Class / Section"
                            styles={selectStyles}
                            isSearchable={true}
                            isClearable={true}
                          />
                        </div>
                        <div style={{ width: "180px" }}>
                          <Select
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                            options={statusOptions}
                            placeholder="Status"
                            styles={selectStyles}
                            isSearchable={false}
                            isClearable={true}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col sm={6} md={7} className="text-sm-end">
                      <div className="d-flex align-items-center justify-content-sm-end justify-content-start gap-2">
                        <div className="position-relative" style={{ width: "260px" }}>
                          <Input
                            type="text"
                            placeholder="Search student or code..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{ paddingRight: "30px", borderRadius: "4px" }}
                          />
                          {searchQuery && (
                            <i
                              className="bx bx-x position-absolute"
                              onClick={() => setSearchQuery("")}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                fontSize: "18px",
                                color: "#74788d",
                                zIndex: 10,
                              }}
                            />
                          )}
                        </div>
                        <Dropdown
                          isOpen={exportDropdownOpen}
                          toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                        >
                          <DropdownToggle
                            color="primary"
                            className="btn btn-primary"
                            caret
                            disabled={isExporting}
                          >
                            {isExporting ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Exporting...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-download me-1" />
                                Export
                              </>
                            )}
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem onClick={() => handleExport("excel")}>
                              <i className="bx bx-file me-2 text-success" />
                              Excel
                            </DropdownItem>
                            <DropdownItem onClick={() => handleExport("pdf")}>
                              <i className="bx bxs-file-pdf me-2 text-danger" />
                              PDF
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading promoted students…</p>
                      </div>
                    ) : (
                      <Table className="table align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Student</th>
                            <th>Current Class / Sec</th>
                            <th>Next Class / Sec</th>
                            <th>Result Status</th>
                            <th>Fee Due</th>
                            <th>Teacher's Remark</th>
                            <th>Admin's Remark</th>
                            <th>Promoted By</th>
                            <th>Promotion Status</th>
                            <th>Promoted Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.length > 0 ? (
                            students.map(student => (
                              <tr key={student.id}>
                                <td>
                                  <div>
                                    <h5 className="font-size-14 mb-1 text-dark">
                                      {student.st_name || "—"}
                                    </h5>
                                    <p className="text-muted font-size-12 mb-0">
                                      Code: {student.st_code}
                                    </p>
                                  </div>
                                </td>
                                <td>
                                  <span className="font-size-13 text-uppercase">
                                    {student.current_class || "—"} - {student.current_sec || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span className="font-size-13 text-uppercase">
                                    {student.next_class || "—"} - {student.next_sec || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      backgroundColor: "#f0f0f0",
                                      color: "#6c757d",
                                      borderRadius: "20px",
                                      padding: "4px 12px",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {student.result_status || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "6px 14px",
                                      borderRadius: "4px",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      border: student.fee_due_amount === 0
                                        ? "1px solid #34c38f"
                                        : "1px solid #f1b44c",
                                      backgroundColor: student.fee_due_amount === 0
                                        ? "#e6f8f0"
                                        : "#fef8e8",
                                      color: student.fee_due_amount === 0
                                        ? "#1a7a54"
                                        : "#946c00",
                                    }}
                                  >
                                    {student.fee_due_amount === 0 ? "Clear" : `₹${student.fee_due_amount}`}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    maxWidth: "220px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  <span className="text-muted">{student.teacher_remark || "—"}</span>
                                </td>
                                <td
                                  style={{
                                    maxWidth: "220px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  <span className="text-dark font-size-13">
                                    {student.admin_remark || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span className="font-size-13">
                                    {student.promoted_by || "—"}
                                  </span>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      student.promotion_status === "Rejected"
                                        ? "danger"
                                        : "success"
                                    }
                                    className="font-size-12 px-3 py-2"
                                  >
                                    {student.promotion_status || "Approved"}
                                  </Badge>
                                </td>
                                <td>
                                  <span className="text-muted font-size-12">
                                    {student.updated_date || student.created_date || "—"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={10} className="text-center py-4 text-muted">
                                No promoted students found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && renderPagination()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default PromotedStudentReport
